<template>
  <b-overlay :show="loading">
    <div class="d-flex justify-content-center align-items-center">
      <div>
        <b-button
          variant="secondary"
          class="btn-icon rounded-circle p-3"
          size="sm"
          data-cy="actions"
          v-on:click="onOptionDetailReceipt(receipt)"
        >
          <feather-icon icon="SearchIcon" />
        </b-button>
      </div>
      <slot name="action-list" v-bind:receipt="receipt">
        <div v-tooltip="'Desplegar acciones'">
          <b-dropdown
            data-cy="gear-button"
            v-has-permission="'ED201'"
            variant="link"
            no-caret
            right
          >
            <template #button-content>
              <feather-icon
                width="24px"
                height="24px"
                icon="SettingsIcon"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-on:click="onOptionReceiptBinnacle(receipt)"
              data-cy="binnacle"
            >
              <vs-icon icon="list"/>
              Bitácora
            </b-dropdown-item>
            <CheckPermission permission="ED182">
              <b-dropdown-item
                @click="onOptionVisualizeReceipt(receipt)"
                data-cy="visualize"
              >
                <vs-icon icon="search"/>
                Ver comprobante
              </b-dropdown-item>
            </CheckPermission>
            <CheckPermission permission="ED182">
              <b-dropdown-item
                v-if="receipt.estado === receiptsCodeStates.facturado"
                @click="onOptionVisualizeInvoice(receipt)"
                data-cy="visualize-invoice"
              >
                <vs-icon icon="search"/>
                Ver factura
              </b-dropdown-item>
            </CheckPermission>
            <CheckPermission permission="ED183">
              <b-dropdown-item
                v-if="receipt.estado === receiptsCodeStates.emitido"
                @click="onOptionInvoiceReceipt(receipt)"
                data-cy="invoice"
              >
                <vs-icon icon="description"/>
                Facturar
              </b-dropdown-item>
            </CheckPermission>
            <CheckPermission permission="ED228">
              <b-dropdown-item
                v-if="canCancelReceipt"
                @click="onOptionCancelReceipt(receipt)"
                data-cy="cancel"
              >
                <vs-icon icon="cancel"/>
                Anular
              </b-dropdown-item>
            </CheckPermission>
          </b-dropdown>
          <span />
        </div>
      </slot>
    </div>
    <b-modal
      :id="`receipt-detail-${receipt.comprobanteId}`"
      :title="`Detalles del comprobante Nº ${receipt.folio}`"
      no-close-on-backdrop
      :ref="`receipt-detail-${receipt.comprobanteId}`"
      size="lg"
      hide-footer
    >
      <DetailReceipt :receipt="receipt" />
    </b-modal>
    <b-modal
      :id="`receipt-cancel-${receipt.comprobanteId}`"
      :ref="`receipt-cancel-${receipt.comprobanteId}`"
      :title="`Anular Comprobante N° ${receipt.folio}`"
      hide-footer
    >
      <ReceiptCancel
        :receipt="receipt"
        @cancel="handleOnCancel"
      />
    </b-modal>
    <b-modal
      :id="`invoice-receipt-${receipt.comprobanteId}`"
      :title="`Facturar comprobante Nº ${receipt.folio}`"
      no-close-on-backdrop
      :ref="`invoice-receipt-${receipt.comprobanteId}`"
      size="sm"
      hide-footer
    >
      <b-overlay :show="loadingReceipt">
        <b-form data-vv-scope="invoice-form">
          <b-form-group
            label="Fecha de emisión (*)"
          >
            <date-picker
              name="issueDate"
              v-model="invoice.issueDate"
              class="w-100"
              format="DD MMM YYYY"
              value-type="YYYY-MM-DD"
              type="date"
              data-vv-as="fecha de emisión"
              placeholder="Fecha de emisión"
              v-validate="'required'"
              data-cy="issue-date-invoice"
            />
            <div v-if="errors.has('invoice-form.issueDate')">
              <small class="text-danger">
                {{ errors.first('invoice-form.issueDate') }}
              </small>
            </div>
          </b-form-group>
        </b-form>
        <div class="text-right mt-3">
          <b-button
            data-cy="invoice-receipt"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="secondary"
            class="mr-2"
            v-on:click="invoiceReceipt(true)"
          >
            Previsualizar
          </b-button>
          <b-button
            data-cy="invoice-receipt"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            v-on:click="invoiceReceipt(false)"
          >
            Facturar
          </b-button>
        </div>
      </b-overlay>
    </b-modal>
    <ModalBinnacle
      :title="`Bitácora del Comprobante Nº ${receipt.folio}`"
      :id="`binnacle-${receipt.comprobanteId}`"
      :ref="`binnacle-${receipt.comprobanteId}`"
    />
  </b-overlay>
</template>
<script>

import { mapActions, mapGetters } from 'vuex';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/es';

import CheckPermission from '@/febos/global/usuario/components/CheckPermission';
import DetailReceipt from '@/febos/aduanas/receipts/views/components/DetailReceipt';
import ModalBinnacle from '@/febos/aduanas/binnacles/ModalBinnacle';
import { getReceipt } from '@/febos/servicios/api/aduanas/receipts.api';
import { receiptsCodeStates, CANCEL_STATES } from '@/febos/aduanas/receipts/views/utils/constants';
import { ALERT_POSITION } from '@/febos/global/utils/configurations';
import ReceiptCancel from '@/febos/aduanas/receipts/views/components/modals/ReceiptCancel';
import { batchesCodeSates } from '@/febos/aduanas/batches/views/utils/constants';
import { clGetDte } from '@/febos/servicios/api/dte.api';

export default {
  components: {
    CheckPermission,
    DetailReceipt,
    ModalBinnacle,
    ReceiptCancel,
    DatePicker
  },
  props: [
    'receipt',
    'selectReceipt'
  ],
  data() {
    return {
      loadingReceipt: false,
      loading: false,
      receiptsCodeStates,
      invoice: {},
      CANCEL_STATES,
      batchesCodeSates
    };
  },
  computed: {
    ...mapGetters('Empresas', [
      'company'
    ]),
    ...mapGetters('Usuario', [
      'currentUserId'
    ]),
    canCancelReceipt() {
      return (this.CANCEL_STATES.includes(this.receipt.estado)
        && this.receipt.lote.estado !== this.batchesCodeSates.destruido
        && this.receipt.lote.estado !== this.batchesCodeSates.donado);
    }
  },
  methods: {
    ...mapActions('Modals', [
      'showModals'
    ]),
    ...mapActions('receipts', [
      'attemptInvoiceReceipt'
    ]),
    onOptionDetailReceipt(receipt) {
      this.selectReceipt(receipt);
      this.$refs[`receipt-detail-${ receipt.comprobanteId}`].show();
    },
    async invoiceReceipt(preview) {
      const isValid = await this.$validator.validateAll('invoice-form');
      if (!isValid) {
        return;
      }

      const arrayBidderIsNotComplete = ['giro', 'direccion', 'comuna', 'ciudad', 'correo', 'contacto'];
      const keysBidderReceipt = Object.keys(this.receipt.adjudicatarios);
      const isBidderComplete = arrayBidderIsNotComplete.some(
        (key) => keysBidderReceipt.includes(key)
      );
      if (!isBidderComplete && !preview) {
        this.$vs.notify({
          title: 'Error',
          text: 'El adjudicatario no cuenta con los datos necesarios para facturar',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger',
          position: ALERT_POSITION
        });
        return;
      }

      if (preview) {
        this.$vs.notify({
          title: 'Hola!',
          text: 'Danos unos momentos para generar el documento',
          color: 'success',
          time: 5000,
          position: 'top-center'
        });
      }
      this.loadingReceipt = true;
      const response = await this.attemptInvoiceReceipt({
        receiptId: this.receipt.comprobanteId,
        issueDate: this.invoice.issueDate,
        preview: preview ? 'si' : 'no'
      });
      if (!preview) {
        await this.$vs.notify({
          title: 'Genial!',
          text: 'Se ha facturado el comprobante',
          color: 'success',
          time: 5000,
          position: 'top-center'
        });
      }
      this.loadingReceipt = false;
      if (!preview) {
        this.invoice = {};
        this.$refs[`invoice-receipt-${this.receipt.comprobanteId}`].hide();
      } else {
        await window.open(response.linkPDF, '_blank');
      }
    },
    onOptionCancelReceipt(receipt) {
      this.selectReceipt(receipt);
      this.$refs[`receipt-cancel-${receipt.comprobanteId}`].show();
    },
    handleOnCancel() {
      this.$refs[`receipt-cancel-${this.receipt.comprobanteId}`].hide();
      this.$emit('reload-list');
    },
    onOptionInvoiceReceipt(receipt) {
      this.invoice = {};
      this.selectReceipt(receipt);
      this.$refs[`invoice-receipt-${receipt.comprobanteId}`].show();
    },
    onOptionReceiptBinnacle(receipt) {
      this.selectReceipt(receipt);
      this.$refs[`binnacle-${receipt.comprobanteId}`].show(receipt.comprobanteId);
    },
    async onOptionVisualizeInvoice(receipt) {
      this.$vs.notify({
        title: 'Hola!',
        text: 'Estamos buscando el archivo, danos unos segundos',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'primary',
        position: ALERT_POSITION
      });
      const { data } = await clGetDte({
        febosId: receipt.dteId,
        pdf: 'si',
        imagen: 'si',
        incrustar: 'no',
        regenerar: 'no',
        tipoImagen: 0
      });
      await window.open(data.imagenLink);
    },
    async onOptionVisualizeReceipt(receipt) {
      this.$vs.notify({
        title: 'Hola!',
        text: 'Estamos buscando el archivo, danos unos segundos',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'primary',
        position: ALERT_POSITION
      });
      const { data } = await getReceipt({
        receiptId: receipt.comprobanteId,
        params: {
          pdf: 'si'
        }
      });
      await window.open(data.url, '_blank');
    }
  }
};
</script>

<style>
.mx-input {
  padding: 20px 10px 20px 35px;
  color: #6e6b7b;
}
.mx-icon-calendar {
  left: 10px
}
</style>
